import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { allQuests, artists, communityFeed } from "@/mocks/artistData";
import Navbar from "@/pages/home/components/Navbar";

const colorMap: Record<string, { glow: string; bg: string; border: string; text: string }> = {
  cyan:    { glow: "#00f5d4", bg: "bg-[#00f5d4]/10", border: "border-[#00f5d4]/30", text: "text-[#00f5d4]" },
  magenta: { glow: "#f72585", bg: "bg-[#f72585]/10", border: "border-[#f72585]/30", text: "text-[#f72585]" },
  yellow:  { glow: "#ffd60a", bg: "bg-[#ffd60a]/10", border: "border-[#ffd60a]/30", text: "text-[#ffd60a]" },
  green:   { glow: "#39ff14", bg: "bg-[#39ff14]/10", border: "border-[#39ff14]/30", text: "text-[#39ff14]" },
  orange:  { glow: "#ff6b35", bg: "bg-[#ff6b35]/10", border: "border-[#ff6b35]/30", text: "text-[#ff6b35]" },
  purple:  { glow: "#b44fff", bg: "bg-[#b44fff]/10", border: "border-[#b44fff]/30", text: "text-[#b44fff]" },
};

const difficultyLabel: Record<string, string> = { Easy: "입문", Medium: "중급", Hard: "고급" };
const difficultyColor: Record<string, string> = {
  Easy:   "text-[#39ff14] bg-[#39ff14]/10 border-[#39ff14]/30",
  Medium: "text-[#ffd60a] bg-[#ffd60a]/10 border-[#ffd60a]/30",
  Hard:   "text-[#f72585] bg-[#f72585]/10 border-[#f72585]/30",
};

const steps = [
  { step: 1, title: "IP 이미지 선택", desc: "아티스트의 IP 이미지 중 영상에 활용할 캐릭터를 선택하세요.", icon: "ri-image-line" },
  { step: 2, title: "AI 도구 선택", desc: "Seedance, Runway, Pika 등 원하는 AI 영상 제작 도구를 선택하세요.", icon: "ri-robot-line" },
  { step: 3, title: "프롬프트 작성", desc: "캠페인 미션에 맞는 영상 프롬프트를 작성하고 AI로 생성하세요.", icon: "ri-edit-line" },
  { step: 4, title: "영상 제출", desc: "완성된 영상을 커뮤니티에 공유하고 XP를 획득하세요.", icon: "ri-upload-cloud-line" },
];

export default function QuestDetailPage() {
  const { questId } = useParams<{ questId: string }>();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [activeIPIdx, setActiveIPIdx] = useState(0);

  const quest = allQuests.find((q) => q.id === questId);
  if (!quest) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/40 text-lg mb-4">캠페인을 찾을 수 없습니다.</p>
          <Link to="/quests" className="text-[#00f5d4] hover:underline cursor-pointer">캠페인 목록으로</Link>
        </div>
      </div>
    );
  }

  const artist = artists.find((a) => a.id === quest.artistId)!;
  const c = colorMap[quest.color] ?? colorMap.cyan;
  const relatedQuests = allQuests.filter((q) => q.artistId === quest.artistId && q.id !== quest.id).slice(0, 3);
  const relatedFeed = communityFeed.filter((f) => f.artistId === quest.artistId).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />

      {/* Hero Banner */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={quest.thumbnail}
            alt={quest.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f]/95 via-[#0a0a0f]/75 to-[#0a0a0f]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-[#0a0a0f]/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-white/40 mb-8">
            <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">홈</Link>
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs" /></div>
            <Link to="/quests" className="hover:text-white/70 transition-colors cursor-pointer">캠페인</Link>
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs" /></div>
            <span className="text-white/70 line-clamp-1">{quest.title}</span>
          </div>

          <div className="flex flex-col lg:flex-row gap-10 items-start">
            {/* Left */}
            <div className="flex-1">
              {/* Tags Row */}
              <div className="flex items-center gap-3 mb-5 flex-wrap">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${difficultyColor[quest.difficulty]}`}>
                  {"★".repeat(quest.difficultyStars)}{"☆".repeat(5 - quest.difficultyStars)} {difficultyLabel[quest.difficulty]}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text} border ${c.border}`}>
                  {quest.tag}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-white/60 border border-white/20 flex items-center gap-1">
                  <i className="ri-time-line text-xs" /> {quest.deadline}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">{quest.title}</h1>
              <p className="text-white/60 text-base leading-relaxed mb-8 max-w-2xl">{quest.description}</p>

              {/* Artist Info */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-2xl overflow-hidden border-2 flex-shrink-0" style={{ borderColor: `${artist.accentColor}80` }}>
                  <img src={artist.avatar} alt={artist.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-sm">{artist.name}</span>
                    {artist.verified && <i className="ri-verified-badge-fill text-sm" style={{ color: artist.accentColor }} />}
                  </div>
                  <span className="text-white/40 text-xs">{artist.tagline}</span>
                </div>
                <Link
                  to="/artists"
                  className="ml-auto px-4 py-1.5 rounded-full border border-white/20 text-white/60 text-xs hover:text-white hover:border-white/40 transition-all cursor-pointer whitespace-nowrap"
                >
                  아티스트 보기
                </Link>
              </div>

              {/* Stats Row */}
              <div className="flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {quest.participantAvatars.map((av, i) => (
                      <div key={i} className="w-7 h-7 rounded-full overflow-hidden border-2 border-[#0a0a0f]">
                        <img src={av} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                  <span className="text-white/50 text-sm">{quest.participants.toLocaleString()}명 참여 중</span>
                </div>
                <div className="flex items-center gap-1.5 text-white/50 text-sm">
                  <i className="ri-coin-line" style={{ color: c.glow }} />
                  <span className="font-bold" style={{ color: c.glow }}>{quest.reward.toLocaleString()} {quest.rewardType}</span>
                  <span className="text-white/30">보상</span>
                </div>
              </div>
            </div>

            {/* Right - Action Card */}
            <div className="w-full lg:w-80 flex-shrink-0">
              <div
                className="rounded-2xl border bg-[#12121a]/90 backdrop-blur-sm p-6 flex flex-col gap-5"
                style={{ borderColor: `${c.glow}40` }}
              >
                {/* Progress */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white/60 text-sm">캠페인 진행률</span>
                    <span className="font-bold text-sm" style={{ color: c.glow }}>{quest.progress}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${quest.progress}%`, background: `linear-gradient(90deg, ${c.glow}99, ${c.glow})` }}
                    />
                  </div>
                  <div className="text-white/30 text-xs mt-1">{quest.participants.toLocaleString()}명이 도전 중</div>
                </div>

                {/* Reward */}
                <Link
                  to="/rewards"
                  className="rounded-xl p-4 flex items-center gap-3 group transition-all hover:opacity-90 cursor-pointer"
                  style={{ background: `${c.glow}12`, border: `1px solid ${c.glow}30` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: `${c.glow}20` }}>
                    <i className="ri-coin-fill text-xl" style={{ color: c.glow }} />
                  </div>
                  <div className="flex-1">
                    <div className="text-white/50 text-xs">완료 보상</div>
                    <div className="font-black text-xl" style={{ color: c.glow }}>{quest.reward.toLocaleString()} {quest.rewardType}</div>
                  </div>
                  <div className="flex items-center gap-1 text-white/30 text-xs group-hover:text-white/60 transition-colors whitespace-nowrap">
                    <span>리워드 보기</span>
                    <i className="ri-arrow-right-s-line text-sm" />
                  </div>
                </Link>

                {/* CTA */}
                <button
                  onClick={() => navigate(`/quests/${quest.id}/create`)}
                  className="w-full py-4 rounded-xl font-bold text-base text-white transition-all duration-200 hover:opacity-90 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  style={{ background: `linear-gradient(135deg, ${c.glow}, ${artist.secondaryColor})` }}
                >
                  <i className="ri-play-circle-line text-xl" />
                  AI 영상 제작 시작
                </button>

                {/* Secondary Actions */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setLiked(!liked)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      liked ? "border-[#f72585]/50 bg-[#f72585]/10 text-[#f72585]" : "border-white/15 text-white/50 hover:border-white/30 hover:text-white/80"
                    }`}
                  >
                    <i className={liked ? "ri-heart-fill" : "ri-heart-line"} />
                    찜하기
                  </button>
                  <button
                    onClick={() => setBookmarked(!bookmarked)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      bookmarked ? "border-[#ffd60a]/50 bg-[#ffd60a]/10 text-[#ffd60a]" : "border-white/15 text-white/50 hover:border-white/30 hover:text-white/80"
                    }`}
                  >
                    <i className={bookmarked ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                    저장
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-6 pb-24 space-y-16">

        {/* IP Gallery */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: `${c.glow}20` }}>
              <i className="ri-image-line text-sm" style={{ color: c.glow }} />
            </div>
            <h2 className="text-white font-bold text-xl">활용 가능한 IP 이미지</h2>
            <span className="text-white/30 text-sm">{artist.name}의 IP 컬렉션</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {artist.ipImages.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveIPIdx(i)}
                className="relative rounded-2xl overflow-hidden border-2 transition-all duration-200 cursor-pointer group"
                style={{ borderColor: activeIPIdx === i ? c.glow : "rgba(255,255,255,0.08)", aspectRatio: "1/1" }}
              >
                <img src={img} alt={`IP ${i + 1}`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                {activeIPIdx === i && (
                  <div className="absolute inset-0 flex items-center justify-center" style={{ background: `${c.glow}20` }}>
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                  </div>
                )}
                <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-black/60 backdrop-blur-sm">
                  <span className="text-white text-[10px] font-medium">IP #{i + 1}</span>
                </div>
              </button>
            ))}
          </div>
          <p className="text-white/30 text-xs mt-3">
            <i className="ri-information-line mr-1" />
            IP 이미지를 선택하면 AI 영상 제작 시 해당 캐릭터가 기본으로 설정됩니다.
          </p>
        </section>

        {/* How to Complete */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: `${c.glow}20` }}>
              <i className="ri-route-line text-sm" style={{ color: c.glow }} />
            </div>
            <h2 className="text-white font-bold text-xl">캠페인 완료 방법</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((s, i) => (
              <div key={s.step} className="relative rounded-2xl border border-white/10 bg-white/5 p-5">
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 -right-2 z-10 w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-white/20 text-lg" />
                  </div>
                )}
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl mb-4"
                  style={{ background: `${c.glow}18`, border: `1px solid ${c.glow}40` }}
                >
                  <i className={`${s.icon} text-lg`} style={{ color: c.glow }} />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: `${c.glow}20`, color: c.glow }}>
                    STEP {s.step}
                  </span>
                </div>
                <h3 className="text-white font-bold text-sm mb-1">{s.title}</h3>
                <p className="text-white/40 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Community Submissions */}
        {relatedFeed.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: `${c.glow}20` }}>
                  <i className="ri-video-line text-sm" style={{ color: c.glow }} />
                </div>
                <h2 className="text-white font-bold text-xl">팬들의 제출 영상</h2>
                <span className="text-white/30 text-sm">{artist.name} 관련</span>
              </div>
              <Link to="/community" className="text-white/40 text-sm hover:text-white/70 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap">
                전체 보기 <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedFeed.map((item) => (
                <div key={item.id} className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden group">
                  <div className="relative" style={{ aspectRatio: "16/9" }}>
                    <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30">
                        <i className="ri-play-fill text-white text-lg" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-black/70 backdrop-blur-sm">
                      <span className="text-white text-xs font-mono">{item.duration}</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                        <img src={item.user.avatar} alt={item.user.name} className="w-full h-full object-cover" />
                      </div>
                      <span className="text-white/60 text-xs">{item.user.name}</span>
                      <span className="ml-auto text-white/30 text-xs">{item.timeAgo}</span>
                    </div>
                    <h4 className="text-white font-semibold text-sm line-clamp-1">{item.title}</h4>
                    <div className="flex items-center gap-3 mt-2 text-white/40 text-xs">
                      <span className="flex items-center gap-1"><i className="ri-heart-line" />{item.likes.toLocaleString()}</span>
                      <span className="flex items-center gap-1"><i className="ri-chat-1-line" />{item.comments}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Related Quests */}
        {relatedQuests.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: `${c.glow}20` }}>
                  <i className="ri-sword-line text-sm" style={{ color: c.glow }} />
                </div>
                <h2 className="text-white font-bold text-xl">{artist.name}의 다른 캠페인</h2>
              </div>
              <Link to="/quests" className="text-white/40 text-sm hover:text-white/70 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap">
                전체 보기 <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedQuests.map((rq) => {
                const rc = colorMap[rq.color] ?? colorMap.cyan;
                return (
                  <Link
                    key={rq.id}
                    to={`/quests/${rq.id}`}
                    className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden hover:border-white/20 transition-all cursor-pointer group"
                  >
                    <div className="relative h-36 overflow-hidden">
                      <img src={rq.thumbnail} alt={rq.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f]/80 via-transparent to-transparent" />
                      <div className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${rc.bg} ${rc.text} ${rc.border}`}>
                        {rq.tag}
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="text-white font-bold text-sm mb-1 line-clamp-1">{rq.title}</h4>
                      <div className="flex items-center justify-between">
                        <span className="text-white/40 text-xs">{rq.participants.toLocaleString()}명 참여</span>
                        <span className="font-bold text-xs" style={{ color: rc.glow }}>{rq.reward.toLocaleString()} XP</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
